/**
 * Shared GHG scope palette and CO₂e disclosure copy for dashboards and reporting.
 * Scope 1: direct (warm). Scope 2: purchased energy (blue). Scope 3: value chain (slate).
 */
export const GHG_SCOPE_COLORS = {
  scope1: { fill: '#c2410c', stroke: '#7c2d12', label: 'Scope 1' },
  scope2: { fill: '#2563eb', stroke: '#1e3a8a', label: 'Scope 2' },
  scope3: { fill: '#475569', stroke: '#1e293b', label: 'Scope 3' },
} as const;

export const GHG_SCOPE_HEX = {
  scope1: GHG_SCOPE_COLORS.scope1.fill,
  scope2: GHG_SCOPE_COLORS.scope2.fill,
  scope3: GHG_SCOPE_COLORS.scope3.fill,
} as const;

/** Shown under chart titles for audit / disclosure alignment */
export const CO2E_METHODOLOGY_SUBTITLE =
  'Values are CO₂-equivalent (CO₂e), AR6 GWP-100, unless your inventory configuration overrides factors.';

export const COMPLIANCE_INVENTORY_LABEL = 'Compliance inventory (ISO 14064 aligned)';

export const CARBON_MODEL_VERSION = '2026-05-13';

export function formatCo2eFromKg(
  kg: number,
  options?: { maximumFractionDigits?: number; unit?: 'auto' | 'kg' | 't' }
): string {
  const n = Number(kg);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const unit = options?.unit || 'auto';
  const useTonnes = unit === 't' || (unit === 'auto' && abs >= 1000);
  const digits = options?.maximumFractionDigits ?? (useTonnes ? 2 : 1);
  if (useTonnes) {
    return `${(n / 1000).toLocaleString('en-IN', { maximumFractionDigits: digits })} t CO₂e`;
  }
  return `${n.toLocaleString('en-IN', { maximumFractionDigits: digits })} kg CO₂e`;
}

/** Y-axis caption when series are stored in kg CO₂e */
export function yAxisLabelForKgDomain(maxKg: number): string {
  if (!Number.isFinite(maxKg) || maxKg <= 0) return 'kg CO₂e';
  return maxKg >= 1000 ? 't CO₂e (1000 kg = 1 t)' : 'kg CO₂e';
}

export function formatAxisTickKg(value: number): string {
  if (!Number.isFinite(value)) return '';
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}t`;
  return `${Math.round(value)}`;
}

/** Largest finite kg value from chart rows (minimum 1 for stable axis labels). */
export function maxKgFromRows<T>(rows: T[], getter: (row: T) => number): number {
  if (!rows.length) return 1;
  const values = rows.map(getter).filter((v) => Number.isFinite(v));
  return Math.max(1, ...values, 0);
}

type RechartsAxisLabelProps = {
  value: string;
  angle: number;
  position: 'insideLeft' | 'insideBottom' | 'insideTop' | 'insideRight';
  fill?: string;
  fontSize: number;
  style?: { fontSize: number; fill?: string };
  offset?: number;
};

/** Vertical value axis label (Y-axis on column charts, X-axis on horizontal bar charts). */
export function rechartsKgValueAxisLabelProps(
  maxKg: number,
  options?: { fill?: string; vertical?: boolean }
): RechartsAxisLabelProps {
  const vertical = options?.vertical ?? true;
  return {
    value: yAxisLabelForKgDomain(maxKg),
    angle: vertical ? -90 : 0,
    position: vertical ? 'insideLeft' : 'insideBottom',
    fill: options?.fill,
    fontSize: 11,
    style: options?.fill ? { fontSize: 11, fill: options.fill } : undefined,
    offset: vertical ? undefined : -4,
  };
}

export function rechartsPercentAxisLabelProps(options?: { fill?: string }): RechartsAxisLabelProps {
  return {
    value: '% of total',
    angle: -90,
    position: 'insideLeft',
    fill: options?.fill,
    fontSize: 11,
    style: options?.fill ? { fontSize: 11, fill: options.fill } : undefined,
  };
}

export function rechartsScoreAxisLabelProps(options?: { fill?: string }): RechartsAxisLabelProps {
  return {
    value: 'Score (0–100)',
    angle: -90,
    position: 'insideLeft',
    fill: options?.fill,
    fontSize: 11,
    style: options?.fill ? { fontSize: 11, fill: options.fill } : undefined,
  };
}

export function rechartsTonnesAxisLabelProps(options?: { fill?: string }): RechartsAxisLabelProps {
  return {
    value: 't CO₂e',
    angle: 0,
    position: 'insideBottom',
    fill: options?.fill,
    fontSize: 11,
    style: options?.fill ? { fontSize: 11, fill: options.fill } : undefined,
    offset: -4,
  };
}

export function rechartsCountAxisLabelProps(options?: { fill?: string }): RechartsAxisLabelProps {
  return {
    value: 'Count',
    angle: -90,
    position: 'insideLeft',
    fill: options?.fill,
    fontSize: 11,
    style: options?.fill ? { fontSize: 11, fill: options.fill } : undefined,
  };
}

/** Recharts tooltip formatter for kg-based emission series. */
export function rechartsEmissionsTooltipFormatter(value: number | string): [string, string] {
  const n = Number(value);
  if (!Number.isFinite(n)) return ['—', ''];
  return [formatCo2eFromKg(n), ''];
}

/** Recharts tooltip formatter for percentage series. */
export function rechartsPercentTooltipFormatter(value: number | string): [string, string] {
  const n = Number(value);
  if (!Number.isFinite(n)) return ['—', ''];
  return [`${n.toFixed(1)}%`, ''];
}

/** Recharts tooltip formatter for count / transaction series. */
export function rechartsCountTooltipFormatter(value: number | string): [string, string] {
  const n = Number(value);
  if (!Number.isFinite(n)) return ['—', ''];
  return [`${Math.round(n).toLocaleString('en-IN')}`, 'Count'];
}

/** Recharts tooltip formatter for 0–100 score series. */
export function rechartsScoreTooltipFormatter(value: number | string): [string, string] {
  const n = Number(value);
  if (!Number.isFinite(n)) return ['—', ''];
  return [`${n.toFixed(1)}`, 'Score (0–100)'];
}

/** Recharts tooltip formatter for tonne-based emission series. */
export function rechartsTonnesTooltipFormatter(value: number | string): [string, string] {
  const n = Number(value);
  if (!Number.isFinite(n)) return ['—', ''];
  return [`${n.toFixed(3)} t CO₂e`, 'Emissions'];
}

/** Centered chart plot area used across carbon analytics surfaces. */
export const centeredChartColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
} as const;

export function chartPlotBoxSx(height: number, maxWidth = 720) {
  return {
    width: '100%',
    maxWidth,
    minWidth: 0,
    mx: 'auto',
    height,
  };
}

export const CHART_UNIT_CAPTIONS = {
  emissionsKg: 'Axis and tooltips use kg CO₂e; values ≥ 1000 kg display as tonnes (t) on ticks.',
  emissionsPercent: 'Values show % of reported total.',
  score: 'Score scale 0–100.',
  count: 'Transaction count per emissions band.',
} as const;

/** Left margin sized for visible kg/t axis unit labels. */
export function cartesianMarginsForKgAxis(maxKg: number) {
  return {
    top: 12,
    right: 20,
    left: maxKg >= 1000 ? 56 : 48,
    bottom: 20,
  };
}

export const pieChartCenterProps = {
  cx: '50%',
  cy: '50%',
} as const;

const SCOPE1_KEYS = /scope\s*1|^s1$/i;
const SCOPE2_KEYS = /scope\s*2|^s2$/i;
const SCOPE3_KEYS = /scope\s*3|^s3$/i;

export function fillHexForScopeLabel(scopeLabel: string | undefined): string {
  const s = String(scopeLabel || '');
  if (SCOPE1_KEYS.test(s)) return GHG_SCOPE_HEX.scope1;
  if (SCOPE2_KEYS.test(s)) return GHG_SCOPE_HEX.scope2;
  if (SCOPE3_KEYS.test(s)) return GHG_SCOPE_HEX.scope3;
  return '#64748b';
}
